import { useEffect, useRef, useState } from 'react';
import { TbLogout } from 'react-icons/tb';
import { useAuth } from './AuthContext';
import { errorMessageFor } from './error-message';

/**
 * The one real logout control (sidebar, drawer, change-password and
 * "acceso no disponible"). `logout()` only clears the session on 204/401 and
 * throws otherwise, so a failure keeps the user signed in and shows a safe
 * message via `errorMessageFor` — never an internal error's raw text.
 * On success the session state flips to anonymous and the route guards take
 * the user back to the login; this component simply unmounts.
 */
export function LogoutButton({ variant = 'dark' }) {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  // Set on every setup, not only at declaration: React.StrictMode runs
  // mount → cleanup → mount in development, and a guard that is only ever
  // cleared would stay `false` and swallow every logout error.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function handleClick() {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      await logout();
    } catch (caught) {
      if (!mounted.current) return;
      setError(errorMessageFor(caught));
      setBusy(false);
    }
  }

  return (
    <div className={`gym-logout gym-logout--${variant}`}>
      <button type="button" className="gym-logout__button" onClick={handleClick} disabled={busy}>
        <TbLogout aria-hidden="true" focusable="false" />
        <span>{busy ? 'Cerrando sesión…' : 'Cerrar sesión'}</span>
      </button>
      {error && <p role="alert" className="gym-logout__error">{error}</p>}
    </div>
  );
}
